import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import type { Stripe } from '@stripe/stripe-js';
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import posthog from '../../lib/posthog';
import { getStripe } from '../../lib/stripe';
import { isNative } from '../../lib/platform';
import { stripeAppearance } from '../../lib/stripeAppearance';
import { quoteTrip } from '../../../supabase/functions/_shared/pricing.ts';
import {
  Button,
  Card,
  Spinner,
  StripeSecureBadge,
} from '../../components/design-system';
import { JourneySidebar } from '../../components/traveler/JourneySidebar';

interface PricingInfo {
  base_day_price_cents: number;
  extra_day_price_cents: number;
  currency: string;
}

interface TripLeg {
  city: string;
  days: number;
}

// One shared empty list: setting a new [] every time would re-render for nothing.
const NO_LEGS: TripLeg[] = [];

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency.toUpperCase(),
    // Whole euros read cleaner (€97), but a price with cents must not be rounded.
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

/** The card + Apple Pay/Google Pay form itself, rendered once the Checkout
 * Session's client secret is ready to mount against. Confirms in-page —
 * `redirect: 'if_required'` means the traveler only ever leaves this page for
 * card networks that mandate it (none of card/Apple Pay/Google Pay do; 3-D
 * Secure, when a bank requires it, opens as an in-page Stripe modal, not a
 * navigation). */
function CheckoutForm({ onPaid }: { onPaid: (sessionId: string) => void }) {
  const checkoutResult = useCheckout();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (checkoutResult.type !== 'success' || submitting) return;
    const { checkout } = checkoutResult;
    setSubmitting(true);
    setError(null);

    try {
      // returnUrl must NOT be passed here — the Checkout Session already
      // has its own return_url baked in from creation time (below), and
      // Stripe rejects confirm() outright if both are set:
      // "You cannot provide `returnUrl` to confirm() when `return_url`
      // was already provided when creating the Checkout Session."
      const result = await checkout.confirm({ redirect: 'if_required' });

      if (result.type === 'error') {
        setError(result.error.message ?? 'Payment failed. Please check your details and try again.');
        setSubmitting(false);
        return;
      }

      const { status } = result.session;
      if (status.type === 'complete' && status.paymentStatus === 'paid') {
        onPaid(result.session.id);
        return;
      }

      // requires_action or similar with no redirect available — rare, but
      // don't leave the traveler staring at a spinner forever.
      setError("We couldn't confirm your payment. Please try again.");
      setSubmitting(false);
    } catch (err) {
      // confirm() throws synchronously (rather than resolving with `error`)
      // when the Checkout session never finished loading. Without this catch
      // the promise rejects unhandled and the button spins forever.
      console.error('checkout.confirm threw:', err);
      setError("We couldn't reach Stripe to process your payment. Please try again.");
      setSubmitting(false);
    }
  }

  if (checkoutResult.type === 'error') {
    return <p className="text-sm text-crimson text-center">{checkoutResult.error.message}</p>;
  }

  const ready = checkoutResult.type === 'success';

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
      <StripeSecureBadge />
      <PaymentElement options={{ layout: 'tabs', wallets: { link: 'never' } }} />
      {error && <p className="text-sm text-crimson text-center">{error}</p>}
      <Button type="submit" className="w-full" disabled={!ready || submitting}>
        {submitting ? <Spinner size="sm" className="text-ink" /> : 'Confirm & pay'}
      </Button>
      <p className="text-xs text-app-muted text-center">Powered by Stripe — your card details never touch our servers.</p>
    </form>
  );
}

export function Payment() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = location.state as { matchId?: string; matchIds?: string[]; portraitId?: string } | null;
  const matchId = nav?.matchId ?? null;
  // A trip with several cities sends one match per city; the charge is one.
  const matchIdsKey = nav?.matchIds && nav.matchIds.length > 1 ? nav.matchIds.join(',') : '';
  const navPortraitId = nav?.portraitId ?? null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tripDays, setTripDays] = useState(3);
  const [pricing, setPricing] = useState<PricingInfo | null>(null);
  const [legs, setLegs] = useState<TripLeg[]>([]);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeFailed, setStripeFailed] = useState(false);
  const [stripeAttempt, setStripeAttempt] = useState(0);
  // Trips with several cities for this traveler (everyone, or admins while testing).
  const [perCity, setPerCity] = useState(false);

  // Load Stripe.js in parallel with the Checkout Session. Content blockers and
  // bad networks block js.stripe.com, so show a retry instead of an empty card.
  useEffect(() => {
    if (!stripeKey) return;
    let cancelled = false;
    setStripeFailed(false);
    getStripe(stripeKey)
      .then((instance) => {
        if (cancelled) return;
        if (instance) setStripe(instance);
        else setStripeFailed(true);
      })
      .catch((err: unknown) => {
        console.error('Stripe.js failed to load:', err);
        posthog.capture('stripe_load_failed', { attempt: stripeAttempt + 1 });
        if (!cancelled) setStripeFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [stripeAttempt]);

  useEffect(() => {
    if (!user) return;

    // Payment is always reached by picking a host on /matches, which passes the
    // chosen match through router state. No match → send them back to choose.
    if (!matchId) {
      navigate('/matches', { replace: true });
      return;
    }

    async function load() {
      // Anchor on the journey the traveler actually came from. MatchSelection
      // passes its portraitId; falling back to "latest by created_at" priced
      // the wrong trip whenever an older portrait existed (the portrait upsert
      // is keyed on conversation_id, so created_at stays at its original
      // value and "latest" isn't necessarily this journey).
      const portraitQuery = supabase
        .from('taste_portraits')
        .select('id, trip_days')
        .eq('traveler_id', user!.id);

      const { data: portrait } = navPortraitId
        ? await portraitQuery.eq('id', navPortraitId).maybeSingle()
        : await portraitQuery.order('created_at', { ascending: false }).limit(1).maybeSingle();

      if (!portrait) {
        navigate('/try', { replace: true });
        return;
      }

      // Already paid for THIS journey? Go straight to matches.
      const { data: paidPayment } = await supabase
        .from('payments')
        .select('id')
        .eq('portrait_id', portrait.id)
        .eq('status', 'paid')
        .limit(1)
        .maybeSingle();

      if (paidPayment) {
        navigate('/matches', { replace: true });
        return;
      }

      setTripDays(portrait.trip_days ?? 3);

      // Get pricing
      const [{ data: settings }, { data: perCityOn }] = await Promise.all([
        supabase
          .from('platform_settings')
          .select('base_day_price_cents, extra_day_price_cents, currency')
          .limit(1)
          .single(),
        // On for everyone, or only for admins while they test: the same answer
        // the checkout function gets.
        supabase.rpc('per_city_trips_on'),
      ]);

      if (settings) setPricing(settings as PricingInfo);
      setPerCity(perCityOn === true);

      // The trip's cities decide the price (the same rule the checkout function
      // applies): per city when per-city pricing is on, otherwise one stay of the
      // days spent in them.
      const { data: legRows } = await supabase
        .from('trip_legs')
        .select('city, days')
        .eq('portrait_id', portrait.id)
        .order('position');
      setLegs((legRows as TripLeg[] | null) ?? NO_LEGS);

      // Start the Checkout Session right away so the form is ready to mount
      // as soon as pricing renders — no separate "confirm" click before it.
      // In the native app window.location.origin is `capacitor://localhost`,
      // not a real HTTPS domain — Stripe rejects that outright. Omit
      // return_url there and let the edge function fall back to APP_URL /
      // the production domain instead (same fix shape as OAuth redirects).
      const { data, error: fnError } = await supabase.functions.invoke('stripe-create-checkout-session', {
        body: {
          portrait_id: portrait.id,
          match_id: matchId,
          ...(matchIdsKey ? { match_ids: matchIdsKey.split(',') } : {}),
          ...(isNative ? {} : { return_url: `${window.location.origin}/payment/success` }),
        },
      });

      if (fnError || !data?.client_secret) {
        let detail = data?.error as string | undefined;
        const ctx = (fnError as { context?: Response })?.context;
        if (!detail && ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            detail = body?.error;
          } catch {
            /* ignore */
          }
        }
        console.error('checkout session failed:', detail, fnError);
        setError(detail ? `Payment error: ${detail}` : 'Failed to start payment. Please try again.');
        setLoading(false);
        return;
      }

      setClientSecret(data.client_secret as string);
      setLoading(false);
    }

    void load();
  }, [user, matchId, matchIdsKey, navPortraitId, navigate]);

  function handlePaid(sessionId: string) {
    setPaid(true);
    // Reuses the same verify-then-route flow as a Stripe-redirected return —
    // it resolves the match and sends the traveler to their trip chat.
    navigate(`/payment/success?session_id=${sessionId}`, { replace: true });
  }

  const appearance = useMemo(() => (clientSecret ? stripeAppearance() : undefined), [clientSecret]);

  if (authLoading || loading) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!pricing) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center px-6">
        <p className="text-sm text-app-muted">Unable to load pricing. Please try again.</p>
      </div>
    );
  }

  if (!stripeKey) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center px-6 text-center">
        <p className="text-sm text-crimson">Payments aren't configured yet. Please contact support.</p>
      </div>
    );
  }

  const quote = quoteTrip({
    perCity,
    legs,
    tripDays,
    baseCents: pricing.base_day_price_cents,
    extraCents: pricing.extra_day_price_cents,
  });
  const total = quote.grossCents;
  const days = quote.tripDays;

  return (
    <div className="h-dvh flex bg-app-bg overflow-hidden">
      <JourneySidebar activeIndex={2} />

      <div className="flex-1 min-w-0 overflow-y-auto flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm flex flex-col gap-6">

        {/* Header */}
        <div className="text-center flex flex-col gap-3">
          <h1 className="font-serif text-page-title text-app-fg text-pretty">Almost there</h1>
          <p className="text-sm text-app-muted leading-relaxed max-w-xs mx-auto text-pretty">
            Your host will confirm shortly. If they can't, we'll find someone just as
            right for you — no extra cost.
          </p>
        </div>

        {/* Price — one line per city for a multi-city trip, otherwise just the total. */}
        <Card className="flex flex-col gap-3">
          {quote.legs.length > 1 && quote.legs.map((leg, i) => (
            <div key={i} className="flex justify-between items-baseline text-sm text-app-muted">
              <span>{legs[i]?.city} · {leg.days} day{leg.days > 1 ? 's' : ''}</span>
              <span>{formatCents(leg.grossCents, pricing.currency)}</span>
            </div>
          ))}
          <div className="flex justify-between items-baseline">
            <span className="font-serif text-lg text-app-fg">{days} day{days > 1 ? 's' : ''}</span>
            <span className="text-lg font-semibold text-gold">{formatCents(total, pricing.currency)}</span>
          </div>
        </Card>

        {error && <p className="text-sm text-crimson text-center">{error}</p>}

        {/* Payment form — mounted in-page, never a redirect. */}
        {clientSecret && !paid && (
          <Card>
            {stripe ? (
              <CheckoutElementsProvider stripe={stripe} options={{ clientSecret, elementsOptions: { appearance } }}>
                <CheckoutForm onPaid={handlePaid} />
              </CheckoutElementsProvider>
            ) : stripeFailed ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <p className="text-sm text-crimson">
                  We couldn't load the payment form. Check your connection, or turn off any content blocker for this site, then try again.
                </p>
                <Button variant="secondary" onClick={() => setStripeAttempt((n) => n + 1)}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            )}
          </Card>
        )}

        <Link
          to="/matches"
          className="text-sm text-app-muted text-center hover:text-app-fg transition-colors"
        >
          Back to hosts
        </Link>
        </div>
      </div>
    </div>
  );
}

export default Payment;
